/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type JSX } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export interface RevSessPoint {
  readonly label: string;
  /** Revenue in rupees (already converted from paise) for a readable axis. */
  readonly revenue: number;
  readonly sessions: number;
}

interface Props {
  readonly data: readonly RevSessPoint[];
  readonly revenueName: string;
  readonly sessionsName: string;
}

/** Combined revenue (area) + sessions (line) over 6 months (Figma "Revenue & Sessions"). */
export function RevenueSessionsChart({ data, revenueName, sessionsName }: Props): JSX.Element {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart
        data={data as RevSessPoint[]}
        margin={{ top: 8, right: 8, bottom: 4, left: -8 }}
      >
        <defs>
          <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5b47db" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#5b47db" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
        <YAxis yAxisId="rev" tick={{ fontSize: 12, fill: '#64748b' }} width={64} />
        <YAxis
          yAxisId="sess"
          orientation="right"
          allowDecimals={false}
          tick={{ fontSize: 12, fill: '#64748b' }}
          width={32}
        />
        <Tooltip />
        <Area
          yAxisId="rev"
          type="monotone"
          dataKey="revenue"
          name={revenueName}
          stroke="#5b47db"
          strokeWidth={2}
          fill="url(#revFill)"
        />
        <Line
          yAxisId="sess"
          type="monotone"
          dataKey="sessions"
          name={sessionsName}
          stroke="#0ea5e9"
          strokeWidth={2}
          dot={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
